import { Center, Loader } from '@mantine/core';
import { Route, Routes, useLocation } from 'react-router';
import { useSeja } from './seja/useSeja';
import { Prijava } from './strani/Prijava';
import { ZamenjavaGesla } from './strani/ZamenjavaGesla';
import { SplosniPogoji } from './strani/SplosniPogoji';
import { Okvir } from './postavitev/Okvir';
import { PregledRacunov } from './strani/PregledRacunov';
import { NiNajdeno } from './strani/NiNajdeno';
import { Zacetna } from './strani/Zacetna';
import { PregledPredracunov } from './strani/PregledPredracunov';
import { PodrobnostiPredracuna } from './strani/PodrobnostiPredracuna';
import { PregledOseb } from './osebe/PregledOseb';
import { PodrobnostiOsebe } from './osebe/PodrobnostiOsebe';
import { Analize } from './analize/Analize';
import { Asistent } from './asistent/Asistent';
import { PodrobnostiRacuna } from './strani/PodrobnostiRacuna';
import { PregledVozil } from './vozila/PregledVozil';
import { PodrobnostiVozila } from './vozila/PodrobnostiVozila';
import { Nastavitve } from './nastavitve/Nastavitve';
import { NovRacun } from './izdaja/NovRacun';
import { NovPredracun } from './izdaja/NovPredracun';
import { Skrbnistvo } from './skrbnistvo/Skrbnistvo';
import { ZakljucekDneva } from './orodja/ZakljucekDneva';
import { IzracunDmv } from './orodja/IzracunDmv';
import { Pomoc } from './pomoc/Pomoc';
import { ClanekPomoci } from './pomoc/ClanekPomoci';
import { JavnaNavodila } from './pomoc/JavnaNavodila';
import { Informacije } from './orodja/Informacije';
import { Spremembe } from './orodja/Spremembe';
import { InstancaSkrbnistvo } from './skrbnistvo/InstancaSkrbnistvo';
import { Narocnina } from './narocnina/Narocnina';
import { Registracija } from './registracija/Registracija';
import { PotrditevRegistracije } from './registracija/PotrditevRegistracije';
import { Odobritev } from './odobritev/Odobritev';
import { PozabljenoGeslo } from './registracija/PozabljenoGeslo';
import { NovoGeslo } from './registracija/NovoGeslo';
import { SplosniPogojiStran } from './pogoji/SplosniPogojiStran';
import { JavnaZaloga } from './ponudba/JavnaZaloga';
import { JavnoVozilo } from './ponudba/JavnoVozilo';
import { NiPonudbe } from './ponudba/NiPonudbe';
import { VsaPonudba } from './ponudba/VsaPonudba';
import { NapakaApi } from './api/napaka';
import { PregledPovprasevanj } from './povprasevanja/PregledPovprasevanj';
import { PodrobnostiPovprasevanja } from './povprasevanja/PodrobnostiPovprasevanja';
import { PregledPovratnihInformacij } from './povratne/PregledPovratnihInformacij';
import { PodrobnostiPovratneInformacije } from './povratne/PodrobnostiPovratneInformacije';
import { PovratnaSkrbnistvo } from './skrbnistvo/PovratnaSkrbnistvo';
import { SamoDelavnica, SamoPravica, SamoProdaja } from './strani/SamoModul';
import { PregledNalogov } from './delavnica/PregledNalogov';
import { PodrobnostiNaloga } from './delavnica/PodrobnostiNaloga';
import { SprejemVozila } from './delavnica/SprejemVozila';
import { PregledVozilStrank } from './delavnica/PregledVozilStrank';
import { PodrobnostiVozilaStranke } from './delavnica/PodrobnostiVozilaStranke';
import { CenikDelavnice } from './delavnica/CenikDelavnice';
import { KoledarTerminov } from './delavnica/KoledarTerminov';
import { PregledGum } from './delavnica/PregledGum';
import { CenikHotelaGum } from './delavnica/CenikHotelaGum';
import { PodrobnostiKompleta } from './delavnica/PodrobnostiKompleta';

/**
 * Strani brez prijave (registracija, pozabljeno geslo, splošni pogoji, odobritev dodatnih del za stranko delavnice); delujejo tudi, če je kdo v tem brskalniku
 * prijavljen.
 */
const JAVNE: Record<string, () => React.JSX.Element> = {
  '/registracija': Registracija,
  '/registracija/potrditev': PotrditevRegistracije,
  '/pozabljeno-geslo': PozabljenoGeslo,
  '/pozabljeno-geslo/nastavi': NovoGeslo,
  '/splosni-pogoji': SplosniPogojiStran,
  '/odobritev': Odobritev,
};

/**
 * Javne strani ({@link JAVNE}) so dostopne vedno. Brez seje je vsaka druga pot prijava (po prijavi ostane izbrana pot);
 * z začasnim geslom najprej zamenjava gesla, brez sprejetih splošnih pogojev najprej pogoji.
 * Ostale strani so v okvirju z glavo.
 */
export function App() {
  const stanje = useSeja();
  const pot = useLocation().pathname.replace(/\/+$/, '');
  const Javna = JAVNE[pot];
  if (Javna) {
    return <Javna />;
  }
  // navodila brez prijave (povezave iz mailov in novic)
  if (pot === '/navodila' || pot.startsWith('/navodila/')) {
    return <JavnaNavodila />;
  }
  // javna ponudba vozil: vseh prodajalcev in prodajalca (zaloga in vozilo), tudi vgrajena na tujih straneh
  if (pot === '/ponudba' || pot.startsWith('/ponudba/')) {
    return (
      <Routes>
        <Route path="ponudba" element={<VsaPonudba />} />
        <Route path="ponudba/:oznaka" element={<JavnaZaloga />} />
        <Route path="ponudba/:oznaka/:id" element={<JavnoVozilo />} />
        <Route path="*" element={<NiPonudbe napaka={new NapakaApi('', 'NE_OBSTAJA', 404)} ponovi={() => {}} />} />
      </Routes>
    );
  }
  if (stanje.status === 'nalaganje') {
    return (
      <Center h="100vh">
        <Loader aria-label="Nalagam" />
      </Center>
    );
  }
  if (stanje.status === 'odjavljen') {
    return <Prijava />;
  }
  if (stanje.zahtevaSpremembaGesla) {
    return <ZamenjavaGesla />;
  }
  // skrbnik kot uporabnik pogojev ne sprejme namesto njega
  if (!stanje.uporabnik.splosniPogoji && !stanje.prijavaKot) {
    return <SplosniPogoji />;
  }
  return (
    <Routes>
      <Route element={<Okvir />}>
        <Route index element={<Zacetna />} />
        <Route
          path="racuni"
          element={
            <SamoPravica podrocje="RACUNI">
              <PregledRacunov />
            </SamoPravica>
          }
        />
        <Route
          path="racuni/nov"
          element={
            <SamoPravica podrocje="RACUNI" raven="UREJANJE">
              <NovRacun />
            </SamoPravica>
          }
        />
        <Route
          path="racuni/:id"
          element={
            <SamoPravica podrocje="RACUNI">
              <PodrobnostiRacuna />
            </SamoPravica>
          }
        />
        <Route
          path="racuni/:id/uredi"
          element={
            <SamoPravica podrocje="RACUNI" raven="UREJANJE">
              <NovRacun />
            </SamoPravica>
          }
        />
        <Route
          path="predracuni"
          element={
            <SamoPravica podrocje="PREDRACUNI">
              <PregledPredracunov />
            </SamoPravica>
          }
        />
        <Route
          path="predracuni/nov"
          element={
            <SamoPravica podrocje="PREDRACUNI" raven="UREJANJE">
              <NovPredracun />
            </SamoPravica>
          }
        />
        <Route
          path="predracuni/:id"
          element={
            <SamoPravica podrocje="PREDRACUNI">
              <PodrobnostiPredracuna />
            </SamoPravica>
          }
        />
        <Route
          path="osebe"
          element={
            <SamoPravica podrocje="STRANKE">
              <PregledOseb />
            </SamoPravica>
          }
        />
        <Route
          path="osebe/:vrsta/:id"
          element={
            <SamoPravica podrocje="STRANKE">
              <PodrobnostiOsebe />
            </SamoPravica>
          }
        />
        <Route
          path="analize"
          element={
            <SamoProdaja>
              <SamoPravica podrocje="ANALIZE">
                <Analize />
              </SamoPravica>
            </SamoProdaja>
          }
        />
        <Route path="asistent" element={<Asistent />} />
        <Route path="asistent/:id" element={<Asistent />} />
        <Route
          path="vozila"
          element={
            <SamoProdaja>
              <SamoPravica podrocje="VOZILA">
                <PregledVozil />
              </SamoPravica>
            </SamoProdaja>
          }
        />
        <Route
          path="vozila/:id"
          element={
            <SamoProdaja>
              <SamoPravica podrocje="VOZILA">
                <PodrobnostiVozila />
              </SamoPravica>
            </SamoProdaja>
          }
        />
        <Route
          path="povprasevanja"
          element={
            <SamoProdaja>
              <SamoPravica podrocje="PONUDBA">
                <PregledPovprasevanj />
              </SamoPravica>
            </SamoProdaja>
          }
        />
        <Route
          path="povprasevanja/:id"
          element={
            <SamoProdaja>
              <SamoPravica podrocje="PONUDBA">
                <PodrobnostiPovprasevanja />
              </SamoPravica>
            </SamoProdaja>
          }
        />
        <Route
          path="delavnica/gume"
          element={
            <SamoDelavnica>
              <SamoPravica podrocje="DELAVNICA">
                <PregledGum />
              </SamoPravica>
            </SamoDelavnica>
          }
        />
        <Route
          path="delavnica/gume/cenik"
          element={
            <SamoDelavnica>
              <SamoPravica podrocje="DELAVNICA">
                <CenikHotelaGum />
              </SamoPravica>
            </SamoDelavnica>
          }
        />
        <Route
          path="delavnica/gume/:id"
          element={
            <SamoDelavnica>
              <SamoPravica podrocje="DELAVNICA">
                <PodrobnostiKompleta />
              </SamoPravica>
            </SamoDelavnica>
          }
        />
        <Route
          path="delavnica/termini"
          element={
            <SamoDelavnica>
              <SamoPravica podrocje="DELAVNICA">
                <KoledarTerminov />
              </SamoPravica>
            </SamoDelavnica>
          }
        />
        <Route
          path="delavnica/nalogi"
          element={
            <SamoDelavnica>
              <SamoPravica podrocje="DELAVNICA">
                <PregledNalogov />
              </SamoPravica>
            </SamoDelavnica>
          }
        />
        <Route
          path="delavnica/nalogi/:id"
          element={
            <SamoDelavnica>
              <SamoPravica podrocje="DELAVNICA">
                <PodrobnostiNaloga />
              </SamoPravica>
            </SamoDelavnica>
          }
        />
        <Route
          path="delavnica/sprejem"
          element={
            <SamoDelavnica>
              <SamoPravica podrocje="DELAVNICA" raven="UREJANJE">
                <SprejemVozila />
              </SamoPravica>
            </SamoDelavnica>
          }
        />
        <Route
          path="delavnica/vozila"
          element={
            <SamoDelavnica>
              <SamoPravica podrocje="DELAVNICA">
                <PregledVozilStrank />
              </SamoPravica>
            </SamoDelavnica>
          }
        />
        <Route
          path="delavnica/cenik"
          element={
            <SamoDelavnica>
              <SamoPravica podrocje="DELAVNICA">
                <CenikDelavnice />
              </SamoPravica>
            </SamoDelavnica>
          }
        />
        <Route
          path="delavnica/vozila/:id"
          element={
            <SamoDelavnica>
              <SamoPravica podrocje="DELAVNICA">
                <PodrobnostiVozilaStranke />
              </SamoPravica>
            </SamoDelavnica>
          }
        />
        <Route path="povratne-informacije" element={<PregledPovratnihInformacij />} />
        <Route path="povratne-informacije/:id" element={<PodrobnostiPovratneInformacije />} />
        <Route path="nastavitve" element={<Nastavitve />} />
        <Route path="nastavitve/:skupina" element={<Nastavitve />} />
        <Route
          path="zakljucek-dneva"
          element={
            <SamoPravica podrocje="RACUNI">
              <ZakljucekDneva />
            </SamoPravica>
          }
        />
        <Route
          path="izracun-dmv"
          element={
            <SamoProdaja>
              <IzracunDmv />
            </SamoProdaja>
          }
        />
        <Route path="pomoc" element={<Pomoc />} />
        <Route path="pomoc/:clanek" element={<ClanekPomoci />} />
        <Route path="informacije" element={<Informacije />} />
        <Route path="spremembe" element={<Spremembe />} />
        <Route path="narocnina" element={<Narocnina />} />
        <Route path="skrbnistvo" element={<Skrbnistvo />} />
        <Route path="skrbnistvo/:companyId" element={<InstancaSkrbnistvo />} />
        <Route path="skrbnistvo/povratne-informacije/:id" element={<PovratnaSkrbnistvo />} />
        <Route path="*" element={<NiNajdeno />} />
      </Route>
    </Routes>
  );
}
